import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/StatusBadge";
import { Plus, Edit, Power, Eye, EyeOff, Trash2, FileDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const ROLES = {
  admin: "Süper Admin",
  sales: "Satış Personeli",
  accounting: "Muhasebe",
  warehouse: "Depo / Stok",
  viewer: "Sadece Görüntüleme",
};

const empty = () => ({
  full_name: "", email: "", password: "", role: "sales",
  birth_date: "", blood_type: "", company_phone: "",
  personal_phone: "", emergency_contact_name: "", emergency_contact_phone: "",
  address: "", username_optional: "", notes: "", is_active: true,
});

export default function Personnel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty());
  const [showPw, setShowPw] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(empty()); setOpen(true); };
  const openEdit = (u) => {
    setEdit(u);
    setForm({ ...empty(), ...u, password: "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const payload = { ...form };
      if (edit && !payload.password) delete payload.password;
      if (edit) await api.put(`/users/${edit.id}`, payload);
      else await api.post("/users", payload);
      toast.success("Kaydedildi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Bu personeli kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/users/${id}`);
    toast.success("Personel silindi");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="text-[#08285A] font-bold">{items.length}</span> personel
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="download-roles-pdf"
                  onClick={async () => {
                    const token = localStorage.getItem("azd_token");
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/roles-permissions-pdf?auth=${token}`;
                    try {
                      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                      if (!resp.ok) throw new Error("PDF indirilemedi");
                      const blob = await resp.blob();
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = "AZD_Flow_Rol_ve_Yetkiler.pdf";
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                      toast.success("PDF indirildi");
                    } catch (e) { toast.error("PDF indirilemedi"); }
                  }}>
            <FileDown size={14} className="mr-1.5" /> Rol ve Yetkiler PDF
          </Button>
          <Button onClick={openCreate} data-testid="new-personnel-btn" className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> Yeni Personel
          </Button>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Ad Soyad</th>
                <th className="text-left px-4 py-3 font-semibold">E-Posta</th>
                <th className="text-left px-4 py-3 font-semibold">Rol</th>
                <th className="text-left px-4 py-3 font-semibold">Şirket Tel</th>
                <th className="text-left px-4 py-3 font-semibold">Son Giriş</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                    data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#111827]">{u.full_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{u.email}</td>
                  <td className="px-4 py-3"><Badge variant="info">{ROLES[u.role] || u.role}</Badge></td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{u.company_phone || "-"}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{u.last_login_at ? fmtDateTime(u.last_login_at) : "-"}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={u.is_active ? "success" : "default"}>
                      {u.is_active ? "Aktif" : "Pasif"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded">
                      <Edit size={14} />
                    </button>
                    {u.is_active && (
                      <button onClick={() => deactivate(u.id)}
                              data-testid={`delete-user-${u.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                              title="Sil">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{edit ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3 py-2">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Ad Soyad *</label>
              <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="user-fullname" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">E-Posta *</label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">
                Şifre {!edit && "*"} {edit && <span className="text-[#6B7280] font-normal">(değiştirmek için doldurun)</span>}
              </label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={form.password}
                       onChange={(e) => set("password", e.target.value)} data-testid="user-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280]">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Rol *</label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Doğum Tarihi</label>
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Kan Grubu</label>
              <Input value={form.blood_type} onChange={(e) => set("blood_type", e.target.value)} placeholder="A Rh+" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Şirket Telefonu</label>
              <Input value={form.company_phone} onChange={(e) => set("company_phone", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Şahsi Telefon</label>
              <Input value={form.personal_phone} onChange={(e) => set("personal_phone", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Yakın Ad Soyad</label>
              <Input value={form.emergency_contact_name}
                     onChange={(e) => set("emergency_contact_name", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Yakın Telefon</label>
              <Input value={form.emergency_contact_phone}
                     onChange={(e) => set("emergency_contact_phone", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Adres</label>
              <Textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Kullanıcı Kodu</label>
              <Input value={form.username_optional} onChange={(e) => set("username_optional", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Personel Notu</label>
              <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="save-user" className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
